import {
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  cn,
} from "web-design-system";
import { DistributorEmployee } from "creso-core/types";
import { getInitials } from "creso-core/utils";
import capitalize from "lodash/capitalize";
import { AvatarEmployee } from "web-core";
import { InfoIcon } from "lucide-react";
import { TruncatedNameWithTooltip } from "./truncated-name-with-tooltip";

/** Must be non-empty — Select reserves "" for placeholder. */
export const SELECT_NO_EMPLOYEE_SENTINEL = "__none__";

const employeeSelectItemClassName = cn(
  "px-3 py-2 w-full min-w-0",
  "[&>span:last-of-type]:block [&>span:last-of-type]:w-full [&>span:last-of-type]:min-w-0"
);

export function AssignEmployeeSelect({
  employees,
  roleIdToName,
  value,
  onValueChange,
  disabled,
  triggerClassName,
  triggerLabel,
  triggerTextClassName,
  assignedCountKind = "client",
}: {
  employees: DistributorEmployee[];
  roleIdToName: Map<number, string>;
  value: string | undefined;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  triggerClassName: string;
  triggerLabel: string;
  triggerTextClassName: string;
  assignedCountKind?: "client" | "family";
}) {
  return (
    <Select value={value} disabled={disabled} onValueChange={onValueChange}>
      <SelectTrigger className={triggerClassName}>
        <TruncatedNameWithTooltip
          className={triggerTextClassName}
          text={triggerLabel}
        />
      </SelectTrigger>
      <SelectContent
        className={
          "w-[320px] max-h-[min(352px,var(--radix-select-content-available-height))] p-0"
        }
      >
        {assignedCountKind === "family" && (
          <div className="sticky top-0 z-10 -mt-1 bg-popover px-1 py-2">
            <div className="border border-border p-2 rounded-md bg-tag-slate flex gap-2 items-start">
              <InfoIcon className="size-5 text-tag-slate-foreground mt-0.5" />
              <span className="text-sm text-tag-slate-foreground">
                This employee will gain access to all clients in this family.
              </span>
            </div>
          </div>
        )}
        <div className="flex flex-col gap-1 p-1">
          <div className="flex flex-col gap-1">
            {employees.map((e) => {
              const rawRoleName = roleIdToName.get(e.role_id) ?? "Employee";
              const isAdminRole = rawRoleName.toUpperCase() === "ADMIN";
              const roleLabel = capitalize(rawRoleName);

              return (
                <SelectItem
                  className={employeeSelectItemClassName}
                  key={e.id}
                  value={String(e.id)}
                  textValue={e.name}
                >
                  <div className="flex items-center gap-3">
                    <AvatarEmployee
                      size="default"
                      fallback={getInitials(e.name)}
                    />
                    <div className="flex flex-col gap-1">
                      <div className="flex gap-2">
                        <TruncatedNameWithTooltip
                          className="text-sm line-clamp-1 max-w-[148px]"
                          text={e.name}
                        />
                        <Badge
                          variant="custom"
                          className={cn(
                            "shrink-0",
                            isAdminRole
                              ? "text-tag-violet-foreground bg-tag-violet"
                              : "text-tag-slate-foreground bg-tag-slate"
                          )}
                        >
                          {roleLabel}
                        </Badge>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {assignedCountKind === "family"
                          ? Number(e.num_families_assigned ?? 0) > 1
                            ? `${e.num_families_assigned ?? 0} families assigned`
                            : `${e.num_families_assigned ?? 0} family assigned`
                          : Number(e.num_clients_assigned ?? 0) > 1
                            ? `${e.num_clients_assigned} clients assigned`
                            : `${e.num_clients_assigned} client assigned`}
                      </span>
                    </div>
                  </div>
                </SelectItem>
              );
            })}
          </div>
        </div>
        <div className="sticky bottom-0 z-10 border-t border-border bg-popover px-1 py-2 -mb-1">
          <SelectItem
            className="rounded-none px-3"
            value={SELECT_NO_EMPLOYEE_SENTINEL}
            textValue="No one"
          >
            <div className="flex flex-col">
              <span className="text-md">No one</span>
              <span className="text-xs text-muted-foreground">
                Unassign from all
              </span>
            </div>
          </SelectItem>
        </div>
      </SelectContent>
    </Select>
  );
}
